import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, X, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import TwoFactorVerificationModal from './TwoFactorVerificationModal';

const MagicLinkHandler: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [errorDetails, setErrorDetails] = useState('');
  const [magicToken, setMagicToken] = useState<string | null>(null);
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFAUserId, setTwoFAUserId] = useState<number | null>(null);
  const [twoFAMethod, setTwoFAMethod] = useState<'app' | 'sms' | 'both'>('app');
  const [twoFAPhone, setTwoFAPhone] = useState<string | undefined>();

  // Button hover
  const [buttonHovered, setButtonHovered] = useState(false);

  const verifyMagicLink = useCallback(async (token: string, twoFactorCode?: string, twoFactorMethod?: string) => {
    try {
      setStatus('loading');
      setMessage('Verifying your magic link...');

      let url = `/auth/magic/verify/${token}`;
      if (twoFactorCode && twoFactorMethod) {
        url += `?twoFactorCode=${encodeURIComponent(twoFactorCode)}&twoFactorMethod=${encodeURIComponent(twoFactorMethod)}`;
      }

      const response = await fetch(window.location.origin + '/api' + url);
      const data = await response.json();

      if (data.requiresTwoFactor) {
        setRequires2FA(true);
        setTwoFAUserId(data.userId);
        setTwoFAMethod(data.method);
        setTwoFAPhone(data.phoneNumber);
        setMagicToken(token);
        setStatus('loading');
        setMessage('Two-factor authentication required');
        return;
      }

      if (data.success && data.accessToken) {
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        
        setStatus('success');
        setMessage('Successfully authenticated!');
        
        setUser(data.user);
        
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1500);
      } else {
        setStatus('error');
        setMessage('Authentication failed');
        setErrorDetails(data.error || 'Invalid or expired magic link.');
      }
    } catch (err: any) {
      setStatus('error');
      setMessage('Authentication failed');
      setErrorDetails(err.message || 'An unexpected error occurred.');
      console.error('Magic link verification failed:', err);
    }
  }, [setUser, navigate]);

  const handle2FASuccess = async (code: string, method: string) => {
    if (!magicToken) return;
    await verifyMagicLink(magicToken, code, method);
  };

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Invalid magic link');
      setErrorDetails('No authentication token found in the URL.');
      return;
    }

    verifyMagicLink(token);
  }, [searchParams, verifyMagicLink]);

  const handleRetryLogin = () => {
    navigate('/auth', { replace: true });
  };

  // Styles
  const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: 'var(--v2-bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    fontFamily: 'var(--font-sans)',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--v2-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--v2-border)',
    boxShadow: 'var(--shadow-lg)',
    padding: '40px',
    width: '100%',
    maxWidth: '440px',
    textAlign: 'center',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: 'var(--v2-text)',
    margin: '0 0 4px 0',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '0.9375rem',
    color: 'var(--v2-text-tertiary)',
    margin: '0 0 32px 0',
  };

  const iconBoxStyle = (type: 'loading' | 'success' | 'error'): React.CSSProperties => ({
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    backgroundColor: type === 'success' ? 'var(--v2-success-bg)' : type === 'error' ? 'var(--v2-error-bg)' : 'var(--v2-primary-light)',
  });

  const statusMessageStyle = (type: 'loading' | 'success' | 'error'): React.CSSProperties => ({
    fontSize: '1.0625rem',
    fontWeight: 600,
    color: type === 'success' ? 'var(--v2-success)' : type === 'error' ? 'var(--v2-error)' : 'var(--v2-text)',
    margin: '0 0 8px 0',
  });

  const statusSubtextStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    color: 'var(--v2-text-tertiary)',
    margin: 0,
  };

  const alertStyle: React.CSSProperties = {
    padding: '12px 16px',
    borderRadius: 'var(--radius-md)',
    borderLeft: '4px solid var(--v2-error)',
    backgroundColor: 'var(--v2-error-bg)',
    color: 'var(--v2-error)',
    fontSize: '0.875rem',
    textAlign: 'left',
    marginTop: '20px',
    marginBottom: '24px',
  };

  const getButtonStyle = (hovered: boolean): React.CSSProperties => ({
    padding: '12px 24px',
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  });

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <>
            <div style={iconBoxStyle('loading')}>
              <Loader2 size={32} strokeWidth={2} color="var(--v2-primary)" style={{ animation: 'spin 1s linear infinite' }} />
            </div>
            <p style={statusMessageStyle('loading')}>{message}</p>
            <p style={statusSubtextStyle}>Please wait while we verify your authentication...</p>
          </>
        );

      case 'success':
        return (
          <>
            <div style={iconBoxStyle('success')}>
              <Check size={32} strokeWidth={2} color="var(--v2-success)" />
            </div>
            <p style={statusMessageStyle('success')}>{message}</p>
            <p style={statusSubtextStyle}>Redirecting to dashboard...</p>
          </>
        );

      case 'error':
        return (
          <>
            <div style={iconBoxStyle('error')}>
              <X size={32} strokeWidth={2} color="var(--v2-error)" />
            </div>
            <p style={statusMessageStyle('error')}>{message}</p>
            
            <div style={alertStyle}>
              {errorDetails}
            </div>

            <button
              onClick={handleRetryLogin}
              onMouseEnter={() => setButtonHovered(true)}
              onMouseLeave={() => setButtonHovered(false)}
              style={getButtonStyle(buttonHovered)}
            >
              Back to Login
            </button>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h1 style={titleStyle}>Magic Link</h1>
          <p style={subtitleStyle}>Authentication</p>

          {renderContent()}
        </div>
      </div>

      {/* 2FA Verification Modal */}
      {requires2FA && twoFAUserId && (
        <TwoFactorVerificationModal
          open={requires2FA}
          userId={twoFAUserId}
          availableMethods={twoFAMethod}
          phoneNumber={twoFAPhone}
          onSuccess={handle2FASuccess}
          onCancel={() => {
            setRequires2FA(false);
            setStatus('error');
            setMessage('Authentication cancelled');
            setErrorDetails('Two-factor authentication was cancelled.');
          }}
        />
      )}
    </>
  );
};

export default MagicLinkHandler;
